/**
 * PublicPhotoAlbum — Public gallery page at /album/:token.
 *
 * No login. Auto-loads photos from /api/public/photo-album/{token}, groups
 * by room (or Move-In/Move-Out for turns), shows a lightbox on tap, and
 * refreshes every 60s so owners see new photos land as techs upload them.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, ArrowsClockwise, ImageSquare, X, Warning } from "@phosphor-icons/react";

export default function PublicPhotoAlbum() {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [lightbox, setLB]   = useState(null); // index in flat list

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/photo-album/${token}`);
      setData(data); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Album unavailable");
    } finally { setLoad(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const int = setInterval(load, 60_000);
    return () => clearInterval(int);
  }, [load]);

  const groups = useMemo(() => {
    if (!data) return [];
    const map = {};
    for (const p of (data.photos || [])) {
      const k = p.room || (data.is_turn ? (p.phase === "move_out" ? "Move-Out photos" : "Move-In photos") : "General");
      (map[k] ||= []).push(p);
    }
    return Object.entries(map).map(([room, items]) => ({ room, items }));
  }, [data]);

  const flatIndex = useMemo(() => {
    const arr = [];
    for (const g of groups) arr.push(...g.items);
    return arr;
  }, [groups]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md rounded-xl border-2 border-rose-200 bg-rose-50 p-6 text-center">
          <Warning size={40} weight="fill" className="text-rose-500 mx-auto mb-2"/>
          <div className="font-black text-rose-900 text-lg">Link unavailable</div>
          <div className="text-sm text-rose-800 mt-1">{error}</div>
        </div>
      </div>
    );
  }

  const brand = data.brand_color || "#0f172a";

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div style={{ background: brand }} className="text-white p-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <ImageSquare weight="fill" size={26}/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Live Progress Album</div>
            <div className="font-black text-xl truncate">{data.property_address}{data.unit ? ` · ${data.unit}` : ""}</div>
            <div className="text-[11px] opacity-80">
              {data.work_order_number ? `WO ${data.work_order_number} · ` : ""}
              {data.photo_count} photo{data.photo_count === 1 ? "" : "s"} · updates automatically
            </div>
          </div>
          <button onClick={load} className="rounded-full bg-white/20 hover:bg-white/30 h-9 w-9 flex items-center justify-center"
            data-testid="album-reload">
            <ArrowsClockwise weight="bold" size={16}/>
          </button>
        </div>
      </div>

      {/* Groups */}
      <div className="max-w-4xl mx-auto p-3 space-y-3">
        {groups.length === 0 && (
          <div className="rounded-xl bg-white border-2 border-slate-200 p-8 text-center text-slate-500 text-sm">
            No photos yet — check back soon.
          </div>
        )}
        {groups.map(g => (
          <div key={g.room} className="rounded-xl bg-white border-2 border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-black text-slate-900">{g.room}</div>
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{g.items.length} photo{g.items.length === 1 ? "" : "s"}</div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
              {g.items.map((p, i) => {
                const idx = flatIndex.indexOf(p);
                return (
                  <button key={p.url + i} onClick={() => setLB(idx)}
                    className="aspect-square overflow-hidden rounded-lg bg-slate-100 hover:opacity-90 active:scale-95 transition-transform"
                    data-testid={`album-thumb-${idx}`}>
                    <img src={p.url} alt="" className="w-full h-full object-cover" loading="lazy"/>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-4xl mx-auto p-3 pb-8 text-center text-[10px] text-slate-400" data-testid="public-footer-brand">
        <div className="flex items-center justify-center gap-2 flex-wrap"><b className="text-[#0B1C2D] uppercase tracking-wider">{data.company_name}</b><span>· Powered by</span><img src="/brand/fr-lockup.png?v=10" alt="FieldRadar.pro" className="h-4 w-auto"/></div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && flatIndex[lightbox] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLB(null)}
          data-testid="album-lightbox">
          <button className="absolute top-3 right-3 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setLB(null); }}>
            <X size={28}/>
          </button>
          <button className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 disabled:opacity-30"
            disabled={lightbox === 0}
            onClick={(e) => { e.stopPropagation(); setLB(Math.max(0, lightbox - 1)); }}>
            <ArrowLeft size={28} weight="bold"/>
          </button>
          <img src={flatIndex[lightbox].url} alt="" className="max-h-[90vh] max-w-[92vw] object-contain"/>
          <button className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 disabled:opacity-30"
            disabled={lightbox >= flatIndex.length - 1}
            onClick={(e) => { e.stopPropagation(); setLB(Math.min(flatIndex.length - 1, lightbox + 1)); }}>
            <ArrowRight size={28} weight="bold"/>
          </button>
          <div className="absolute bottom-3 left-0 right-0 text-center text-white/80 text-xs">
            {flatIndex[lightbox].room || "General"} · {lightbox + 1} / {flatIndex.length}
          </div>
        </div>
      )}
    </div>
  );
}
